import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, {
  setTokens,
  clearTokens,
  loadPersistedTokens,
  onAuthFailure,
} from '../api.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  hasPermission: () => false,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
});

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/auth/me');
      setUser(data);
    } catch (error) {
      window.dispatchEvent(new CustomEvent('starter:api-warning', {
        detail: { source: 'auth', message: apiErrorMessage(error, 'Session utilisateur indisponible.') },
      }));
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    const { access } = loadPersistedTokens();
    onAuthFailure(() => {
      setUser(null);
    });
    if (access) {
      fetchMe();
    } else {
      setReady(true);
    }
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/v1/auth/login', { email, password });
    setTokens({ access: data.token, refresh: data.refresh_token });
    await fetchMe();
    return data;
  }, [fetchMe]);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    ready,
    isAuthenticated: !!user,
    hasPermission: (permission) => {
      if (!user) return false;
      if (!permission) return true;
      if (Array.isArray(user.permissions) && user.permissions.includes(permission)) return true;
      if (Array.isArray(user.roles) && (user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_SUPER_ADMIN'))) {
        return true;
      }
      return false;
    },
    login,
    logout,
    refresh: fetchMe,
  }), [user, ready, login, logout, fetchMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
